import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  Inventory2,
  Payment,
  People,
  Assessment,
  Warning,
  ShoppingCart,
  BarChart,
  Refresh,
  Download,
  Info
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import ReportCard from './ReportCard';
import DateRangeFilter from './DateRangeFilter';
import './ReportsDashboard.css';

const ReportsDashboard = ({ onPageChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadDashboardStats();
  }, [filters]);

  const loadDashboardStats = async () => {
    setLoading(true);
    setError('');
    try {
      const [
        dailySales,
        inventory,
        lowStock,
        pendingOrders,
        netProfit
      ] = await Promise.all([
        reportService.getDailySalesAmount(filters),
        reportService.getInventoryReport(),
        reportService.getLowStockAlerts(),
        reportService.getPendingEcommerceOrders(),
        reportService.getNetProfit(filters)
      ]);

      setStats({
        totalRevenue: dailySales.data.summary.total_sales,
        totalOrders: dailySales.data.summary.total_transactions,
        lowStockItems: inventory.data.low_stock_items,
        pendingEcommerceOrders: pendingOrders.data.total_pending_orders,
        netProfit: netProfit.data.profit_breakdown.net_profit,
        criticalAlerts: lowStock.data.summary.critical_alerts,
        posRevenue: dailySales.data.summary.total_pos_sales,
        ecommerceRevenue: dailySales.data.summary.total_ecommerce_sales
      });
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load dashboard statistics: ' + (err.message || 'Unknown error'));
      console.error('Dashboard loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportDashboard = () => {
    try {
      const exportData = [
        {
          'Metric': 'Total Revenue',
          'Value': `LKR ${stats.totalRevenue?.toLocaleString() || '0'}`,
          'Category': 'Sales'
        },
        {
          'Metric': 'Total Orders',
          'Value': stats.totalOrders?.toLocaleString() || '0',
          'Category': 'Sales'
        },
        {
          'Metric': 'Net Profit',
          'Value': `LKR ${stats.netProfit?.toLocaleString() || '0'}`,
          'Category': 'Financial'
        },
        {
          'Metric': 'Low Stock Items',
          'Value': stats.lowStockItems || '0',
          'Category': 'Inventory'
        },
        {
          'Metric': 'Critical Alerts',
          'Value': stats.criticalAlerts || '0',
          'Category': 'Inventory'
        },
        {
          'Metric': 'Pending Orders',
          'Value': stats.pendingEcommerceOrders || '0',
          'Category': 'Ecommerce'
        }
      ];

      if (exportData.length > 0) {
        // Using the excelExport utility
        const excelExport = require('../../../utils/excelExport');
        excelExport.exportToExcel(exportData, 'Dashboard_Overview');
      } else {
        setError('No data available for export');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export dashboard data: ' + err.message);
    }
  };

  const reportCards = [
    {
      title: 'Sales Reports',
      description: 'Daily sales, revenue trends, and transaction analysis across all channels',
      icon: <TrendingUp />,
      path: 'sales-reports',
      color: 'primary',
      metrics: [
        { label: 'Total Revenue', value: `LKR ${stats.totalRevenue?.toLocaleString() || '0'}` },
        { label: 'Total Orders', value: stats.totalOrders?.toLocaleString() || '0' }
      ]
    },
    {
      title: 'Inventory Reports',
      description: 'Stock levels, valuation, low stock alerts, and reorder recommendations',
      icon: <Inventory2 />,
      path: 'inventory-reports',
      color: 'success',
      metrics: [
        { label: 'Low Stock Items', value: stats.lowStockItems || '0' },
        { label: 'Critical Alerts', value: stats.criticalAlerts || '0' }
      ]
    },
    {
      title: 'Financial Reports',
      description: 'Profit & loss analysis, margin tracking, and financial performance insights',
      icon: <Assessment />,
      path: 'financial-reports',
      color: 'warning',
      metrics: [
        { label: 'Net Profit', value: `LKR ${stats.netProfit?.toLocaleString() || '0'}` },
        { label: 'Profit Margin', value: stats.netProfit && stats.totalRevenue ? `${((stats.netProfit / stats.totalRevenue) * 100).toFixed(1)}%` : '0%' }
      ]
    },
    // {
    //   title: 'Customer Analytics',
    //   description: 'Customer behavior, purchase patterns, segmentation, and loyalty analysis',
    //   icon: <People />,
    //   path: 'customer-reports',
    //   color: 'info',
    //   metrics: [
    //     { label: 'New Customers', value: '24' }, // This would come from API
    //     { label: 'Avg Order Value', value: 'LKR 2,450' } // This would come from API
    //   ]
    // },
    {
      title: 'Ecommerce Reports',
      description: 'Online orders performance, delivery tracking, and digital channel analytics',
      icon: <ShoppingCart />,
      path: 'ecommerce-reports',
      color: 'secondary',
      metrics: [
        { label: 'Pending Orders', value: stats.pendingEcommerceOrders || '0' },
        { label: 'Ecommerce Revenue', value: `LKR ${stats.ecommerceRevenue?.toLocaleString() || '0'}` }
      ]
    },
    // {
    //   title: 'Product Performance',
    //   description: 'Top selling products, category analysis, and product margin tracking',
    //   icon: <BarChart />,
    //   path: 'product-reports',
    //   color: 'error',
    //   metrics: [
    //     { label: 'Top Categories', value: '8' }, // This would come from API
    //     { label: 'Products Analyzed', value: '156' } // This would come from API
    //   ]
    // }
  ];

  const renderStatCard = (title, value, subtitle = '', color = 'primary', trend = null) => {
    return (
      <Card className="dash-stat-card">
        <CardContent className="dash-stat-content">
          <div className="dash-stat-header">
            <Typography className="dash-stat-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="dash-stat-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`dash-stat-value dash-stat-${color}`}>
            {value}
          </Typography>
          {trend && (
            <div className={`dash-stat-trend dash-trend-${trend.direction}`}>
              <Typography className="dash-trend-text">
                {trend.value}% {trend.direction === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className="dash-container">
      {/* Header */}
      <Card className="dash-header-card">
        <CardContent className="dash-header-content">
          <div className="dash-header-main">
            <div className="dash-header-text">
              <Typography className="dash-header-title">Reports Dashboard</Typography>
              <Typography className="dash-header-subtitle">
                Comprehensive business intelligence and analytics overview
              </Typography>
            </div>
          </div>
          <div className="dash-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadDashboardStats} 
                disabled={loading}
                className="dash-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExportDashboard}
              disabled={loading}
              className="dash-export-button"
            >
              Export Summary
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="dash-controls-card">
        <CardContent className="dash-controls-content">
          <div className="dash-controls-header">
            <Typography className="dash-controls-title">Date Range</Typography>
            {lastUpdated && (
              <Typography className="dash-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            )}
          </div>
          <DateRangeFilter
            filters={filters}
            onFilterChange={setFilters}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="dash-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadDashboardStats}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="dash-loading-container">
          <CircularProgress size={60} className="dash-loading-spinner" />
          <Typography className="dash-loading-text">
            Loading dashboard data...
          </Typography>
        </div>
      ) : (
        <div className="dash-content">
          {/* Quick Stats Overview */}
          <div className="dash-stats-section">
            <Typography className="dash-section-title">Business Overview</Typography>
            <div className="dash-stats-grid">
              {renderStatCard(
                'Total Revenue',
                `LKR ${stats.totalRevenue?.toLocaleString() || '0'}`,
                'Total revenue from all sales channels',
                'primary'
              )}
              {renderStatCard(
                'Total Orders',
                stats.totalOrders?.toLocaleString() || '0',
                'Total number of transactions',
                'secondary'
              )}
              {renderStatCard(
                'Net Profit',
                `LKR ${stats.netProfit?.toLocaleString() || '0'}`,
                'Net profit after all expenses',
                'success'
              )}
              {renderStatCard(
                'POS Revenue',
                `LKR ${stats.posRevenue?.toLocaleString() || '0'}`,
                'Revenue from physical store sales',
                'info'
              )}
              {renderStatCard(
                'Ecommerce Revenue',
                `LKR ${stats.ecommerceRevenue?.toLocaleString() || '0'}`,
                'Revenue from online sales',
                'warning'
              )}
              {renderStatCard(
                'Pending Orders',
                stats.pendingEcommerceOrders || '0',
                'Ecommerce orders awaiting processing',
                'error'
              )}
            </div>
          </div>

          {/* Inventory Alerts */}
          {(stats.lowStockItems > 0 || stats.criticalAlerts > 0) && (
            <div className="dash-alerts-section">
              <Typography className="dash-section-title">Inventory Alerts</Typography>
              <div className="dash-alerts-grid">
                {stats.criticalAlerts > 0 && (
                  <Card className="dash-alert-card dash-alert-critical">
                    <CardContent className="dash-alert-content">
                      <div className="dash-alert-header">
                        <Warning className="dash-alert-icon" />
                        <Typography className="dash-alert-title">Critical Stock Alerts</Typography>
                      </div>
                      <Typography className="dash-alert-value">
                        {stats.criticalAlerts} items need immediate attention
                      </Typography>
                      <Button 
                        size="small" 
                        className="dash-alert-action"
                        onClick={() => onPageChange('inventory-reports')}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {stats.lowStockItems > 0 && (
                  <Card className="dash-alert-card dash-alert-warning">
                    <CardContent className="dash-alert-content">
                      <div className="dash-alert-header">
                        <Inventory2 className="dash-alert-icon" />
                        <Typography className="dash-alert-title">Low Stock Items</Typography>
                      </div>
                      <Typography className="dash-alert-value">
                        {stats.lowStockItems} items below stock threshold
                      </Typography>
                      <Button 
                        size="small" 
                        className="dash-alert-action"
                        onClick={() => onPageChange('inventory-reports')}
                      >
                        Review Stock
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Report Categories */}
          <div className="dash-reports-section">
            <Typography className="dash-section-title">Analytics Reports</Typography>
            <div className="dash-reports-grid">
              {reportCards.map((report, index) => (
                <div key={index} className="dash-report-item">
                  <ReportCard 
                    {...report} 
                    onPageChange={onPageChange}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default ReportsDashboard;